import React from 'react';
import { Card, CardContent, Typography, Stack, Avatar, Box, IconButton } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IActivityFeedItem } from '../../redux/activity/types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { convertMinuteSecondsDays, formatNumberWithK } from '../../utils/AppUtils';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { EsPlayButton } from '../EsButtons/EsPlayButton';
import EyeIcon from '../CustomSVGIcons/EyeIcon';
import CustomCommentIcon from '../CustomSVGIcons/CustomCommentIcon';
import LoveIcon from '../CustomSVGIcons/LoveIcon';
import { EsFollowButton } from '../EsButtons/EsFollowButton';
import LocalOffer from '@mui/icons-material/LocalOffer';
import Headset from '@mui/icons-material/Headset';
import { UserSummary } from '../../types/global/helper';

export interface IFeedProjectCardProps {
    project: IActivityFeedItem;
    onViewClick?: VoidFunction;
    onCommentClick?: VoidFunction;
    onLikeClick?: VoidFunction;
    onClickPlay?: VoidFunction;
    likingInProgress?: boolean;
}

const FeedProjectCard: React.FC<IFeedProjectCardProps> = ({
    project,
    onCommentClick,
    onLikeClick,
    onViewClick,
    onClickPlay,
    likingInProgress,
}) => {
    const { themeStyle } = useThemeContext();
    const { projects_activity, created_by } = project;
    return (
        <Card
            sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'visible',
                my: 2,
                backgroundColor: theme[themeStyle].secondaryBg,
            }}>
            <CardContent sx={{ px: 0, pb: 0 }}>
                {/* Skill Match Percentage */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
                    <Stack direction="row" alignItems="center" gap={'10px'} width={'100%'}>
                        <ThspianAvatar user={projects_activity?.created_by!} enableDefaultNavigation withMonogram />
                        <Stack>
                            {projects_activity && projects_activity?.created_by && (
                                <Stack direction="row" gap={'3px'}>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 10,
                                        }}>
                                        {projects_activity?.created_by.firstName}
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 10,
                                        }}>
                                        {project.activity_type}
                                    </Typography>
                                </Stack>
                            )}
                            <Stack alignItems="flex-start">
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 8,
                                    }}>
                                    {convertMinuteSecondsDays(project.createdAt)} ago
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack>
                        <EsFollowButton
                            intrinsic
                            isFollowing={created_by.user_following}
                            user={created_by as unknown as UserSummary}
                            location="feed"
                        />
                    </Stack>
                </Stack>
                {projects_activity?.thumbnail && (
                    <Stack sx={{ maxHeight: '350px', overflow: 'hidden', width: '100%', my: 2 }} position="relative">
                        <EsPlayButton onClick={onClickPlay} />
                        <CustomLazyLoadImage src={projects_activity?.thumbnail} style={{ objectFit: 'cover' }} />
                        <Headset
                            sx={{
                                fontSize: 18,
                                color: theme[themeStyle].true_white,
                                position: 'absolute',
                                top: 12,
                                right: 10,
                            }}
                        />
                        {/* Title */}
                        <Stack alignItems="center" px={2} position="absolute" top={8} left={4} direction="row">
                            <Avatar
                                src={projects_activity.created_by?.profile_picture}
                                alt={projects_activity.created_by?.firstName}
                                sx={{
                                    width: 30,
                                    height: 30,
                                    marginRight: 1,
                                    border: `0.5px solid ${theme[themeStyle].true_white}`,
                                }}
                            />
                            <Stack alignItems="flex-start">
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].true_white,
                                        fontSize: 12,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    {projects_activity?.title}
                                </Typography>
                                <Stack alignItems="flex-start">
                                    <Stack direction="row" alignItems="center">
                                        <LocalOffer
                                            sx={{
                                                color: '#237EA2',
                                                // color: theme[themeStyle].true_black,
                                                fontSize: '12px',
                                                mr: 0.5,
                                            }}
                                            style={{
                                                transform: 'scaleX(-1)',
                                            }}
                                        />
                                        {projects_activity?.tag?.map((tag, index) => (
                                            <Stack
                                                bgcolor={'rgba(232, 243, 252, 0.4)'}
                                                key={index}
                                                direction="row"
                                                alignItems="center"
                                                gap={'1px'}
                                                mr={0.5}
                                                p={'2px'}
                                                borderRadius={'2px'}>
                                                <Typography
                                                    style={{
                                                        fontSize: '6px',
                                                        color: theme[themeStyle].true_white,
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        textTransform: 'uppercase',
                                                    }}>
                                                    {tag}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].true_white,
                                            fontSize: 8,
                                            textAlign: 'left',
                                        }}>
                                        {convertMinuteSecondsDays(project.createdAt)} ago
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </CardContent>
            {/* Interactions Section */}
            <Box display="flex" justifyContent="space-around" alignItems="center" p={2}>
                <Box display="flex" alignItems="center">
                    <IconButton
                        sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}
                        onClick={onViewClick}>
                        <EyeIcon stroke={theme[themeStyle].tag_white} height={'20px'} width={'20px'} />
                    </IconButton>
                    <Typography
                        style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white, fontSize: 12 }}
                        marginLeft={1}>
                        {formatNumberWithK(projects_activity?.view_count!)}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <IconButton
                        sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}
                        onClick={onCommentClick}>
                        <CustomCommentIcon stroke={theme[themeStyle].tag_white} height={'20px'} width={'20px'} />
                    </IconButton>
                    <Typography
                        style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white, fontSize: 12 }}
                        marginLeft={1}>
                        {formatNumberWithK(projects_activity?.total_comments!)}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <IconButton
                        sx={{
                            bgcolor: theme[themeStyle].deep_grey,
                            borderRadius: '50%',
                            '&:disabled': {
                                bgcolor: theme[themeStyle].deep_grey,
                                opacity: 0.7,
                            },
                        }}
                        onClick={onLikeClick}
                        disabled={likingInProgress}>
                        <LoveIcon
                            fill={
                                projects_activity?.is_user_liked
                                    ? theme[themeStyle].secondary_red
                                    : theme[themeStyle].tag_white
                            }
                            filled={projects_activity?.is_user_liked}
                            height={'20px'}
                            width={'20px'}
                        />
                    </IconButton>
                    <Typography
                        style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white, fontSize: 12 }}
                        marginLeft={1}>
                        {formatNumberWithK(projects_activity?.total_likes!)}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center">
                    <IconButton sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}>
                        <SignalCellularAltIcon
                            sx={{
                                fontSize: '16px',
                                color: theme[themeStyle].tag_white,
                            }}
                        />
                    </IconButton>
                    <Typography
                        style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].tag_white, fontSize: 12 }}
                        marginLeft={1}>
                        {formatNumberWithK(projects_activity?.trending_marker!)}
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

export { FeedProjectCard };
